/**
 * Leads API service for managing sales leads.
 * @module lib/api/leads.api
 */

import {
  Lead,
  CreateLead,
  UpdateLead,
  PaginatedResponse,
  PaginationParams,
  ImportPreviewRecord,
  ImportOptions,
  ImportResult,
  ExportOptions
} from '@scrambl/shared';
import { BaseApi } from './base-api';
import { useOrganizationStore } from '@/store/organization.store';
import { createSingletonApi } from './singleton-factory';

class LeadApiService extends BaseApi<Lead, CreateLead, UpdateLead> {
  constructor() {
    super({
      baseEndpoint: '/leads',
      responseNormalization: true
    });
  }

  // Override create to add organization ID and backend-required fields
  async create(data: CreateLead): Promise<Lead> {
    const { currentOrganization } = useOrganizationStore.getState();

    const leadData = this.addToOrganizationData(data, currentOrganization?.id);

    // Add backend-specific fields that aren't in the CreateLead type
    const leadDataWithBackendFields = {
      ...leadData,
      deletedAt: null, // Backend requirement
    } as CreateLead;

    return super.create(leadDataWithBackendFields);
  }

  // Alias methods for backward compatibility
  async getLeads(params: Partial<PaginationParams & { name?: string; stage?: string; stageTypeId?: string; columnFilters?: Record<string, unknown> }> = {}): Promise<PaginatedResponse<Lead>> {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.set('page', String(params.page));
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.sortBy) searchParams.set('sortBy', params.sortBy);
    if (params.sortOrder) searchParams.set('sortOrder', params.sortOrder);
    if (params.name) searchParams.set('name', params.name);
    if (params.stage) searchParams.set('stage', params.stage);
    if (params.stageTypeId) searchParams.set('stageTypeId', params.stageTypeId);
    if (params.columnFilters && Object.keys(params.columnFilters).length > 0) {
      searchParams.set('columnFilters', JSON.stringify(params.columnFilters));
    }

    const queryString = searchParams.toString();
    return this.makeRequest<PaginatedResponse<Lead>>(`${this.config.baseEndpoint}?${queryString}`, {
      method: 'GET',
    });
  }

  async getLead(id: string): Promise<Lead> {
    return this.findById(id);
  }

  async createLead(data: CreateLead): Promise<Lead> {
    return this.create(data);
  }

  async updateLead(id: string, data: UpdateLead): Promise<Lead> {
    return this.update(id, data);
  }

  async deleteLead(id: string): Promise<void> {
    return this.delete(id);
  }

  async bulkDeleteLeads(ids: string[]): Promise<{ deletedCount: number }> {
    return this.makeRequest<{ deletedCount: number }>(`${this.config.baseEndpoint}/bulk-delete`, {
      method: 'POST',
      body: { ids }
    });
  }

  // Import/Export operations
  previewImport(records: unknown[], options: ImportOptions): Promise<ImportPreviewRecord[]> {
    return this.makeRequest<ImportPreviewRecord[]>(`${this.config.baseEndpoint}/import/preview`, {
      method: 'POST',
      body: { records, options }
    });
  }

  import(data: { records: ImportPreviewRecord[]; options: ImportOptions }): Promise<ImportResult> {
    return this.makeRequest<ImportResult>(`${this.config.baseEndpoint}/import`, {
      method: 'POST',
      body: data
    });
  }

  export(options: ExportOptions): Promise<Lead[]> {
    return this.makeRequest<Lead[]>(`${this.config.baseEndpoint}/export`, {
      method: 'POST',
      body: options
    });
  }
}

// Export singleton instance using factory
export const leadApi = createSingletonApi(LeadApiService);

// Export for backward compatibility
export { leadApi as LeadApi };
